// src/app/hooks.ts
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';
import type { RootState, AppDispatch } from './store';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useEffect, useRef, useCallback } from 'react';

// Typed Redux hooks
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Router hooks with types
export const useTypedNavigate = () => useNavigate();
export const useTypedLocation = () => useLocation();
export const useTypedParams = <T extends string | Record<string, string | undefined> = Record<string, string | undefined>>() => useParams<T>();

// Custom hook for route state
export function useRouteState<T = unknown>() {
  const location = useLocation();
  return location.state as T | undefined;
}

// Custom hook for previous value
export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

// Custom hook for mounted state
export function useIsMounted(): () => boolean {
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return useCallback(() => isMounted.current, []);
}

// Custom hook for async dispatch
export function useAsyncDispatch() {
  const dispatch = useAppDispatch();
  const isMounted = useIsMounted();

  return useCallback(
    async (action: Parameters<AppDispatch>[0]) => {
      const result = await dispatch(action);
      if (isMounted()) {
        return result;
      }
      throw new Error('Component unmounted');
    },
    [dispatch, isMounted]
  );
}

// Custom hook for auth state
export function useAuth() {
  const auth = useAppSelector((state) => (state as RootState).auth);
  const tenant = useAppSelector((state) => (state as RootState)?.tenant ?? {});
  
  return {
    isAuthenticated: auth.isAuthenticated,
    user: auth.user,
    isLoading: auth.isLoading,
    error: auth.error,
    currentTenant: tenant.current,
  };
}

// Custom hook for UI state
export function useUI() {
  const ui = useAppSelector((state) => state.ui);
  
  return {
    loading: ui.loading,
    errors: ui.errors,
    notifications: ui.notifications,
    // Add UI action dispatchers here
    showNotification: () => {
      // Dispatch notification action
    },
    clearErrors: () => {
      // Dispatch clear errors action
    },
  };
}

// Export all hooks
export {
  useDispatch,
  useSelector,
} from 'react-redux';